import React, { useState, useRef, useCallback, useEffect } from "react";
// @mui
import Link from "@mui/material/Link";
import PropTypes from "prop-types";
// components
import {
  DataGrid as DevextremeDataGrid,
  Scrolling,
  LoadPanel,
  FilterRow,
  HeaderFilter,
  GroupPanel,
  Selection,
  Export,
  ColumnChooser,
  ColumnChooserSearch,
  FilterPanel,
  ColumnFixing,
  StateStoring,
  Sorting,
  RemoteOperations,
  Paging,
} from "devextreme-react/data-grid";
import Column from "src/consuman-components/generic/column";
import { Template } from "devextreme-react/core/template";
import DxLocalStorage from "src/consuman-components/dx-local-storage";

//
import { FIELDTYPE } from "src/core/lib/constants";

// auth
import { useAuthContext } from "src/auth/hooks";

// @mui
import AlertDialog from "src/consuman-components/generic/alert";
// locales
import { useTranslation } from "react-i18next";
// utils
import { exportCallback, installDefaultFilterClear } from "src/utils/devextreme-grid";

// format
import { dateTimeFormatGrid, numberFormat } from "src/core/lib/formats";

//
import { useGet as useGetDynamic } from "src/api/dynamic-configuration";
import ItemsSelectedHandler from "./items-selected-handler";

export default function DataGrid({
  innerRef,
  isActive,
  deletedGender,
  columnName,
  columnCreatedDate,
  columnDeletedDate,
  gridActionRender,
  gridActionRenderWidth = 200,
  gridActionRenderMaxWidth = 200,
  gridActionHeaderRender,
  gridActionsHeaderRender,
  gridActionHeaderMultipleRender,
  gridActionHeaderToggleRender,
  gridActionHeaderInColumn,
  nameGrid,
  height = null,
  entityId,
  customizeColumns,
  storageKey,
  dataSource,
  hrefName,
  type = "normal",
  onInitialized,
  remoteOperation = true,
  selectorMode = false,
  grouping = true,
  children,
  scrolling,
  columnsAutoWidth = true,
  ...props
}) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const itemSelectionRef = useRef(null);

  const storageKeyEnterprise = storageKey ? `${user?.enterpriseCode}${storageKey}` : null;
  const componentRef = useRef(null);
  const initializeGridRef = useRef(true);
  const { dynamicConfigurations: dynamicConfigurationGet, isLoading } = useGetDynamic(entityId);
  const viewportHeightRatio = 0.83;

  const shouldUseViewportHeight = value => value === null || typeof value === "undefined";
  const calcViewportHeight = () => {
    if (typeof window === "undefined") return 600;
    return Math.round(window.innerHeight * viewportHeightRatio);
  };

  const [resolvedHeight, setResolvedHeight] = useState(() =>
    shouldUseViewportHeight(height) ? calcViewportHeight() : height
  );

  useEffect(() => {
    if (!shouldUseViewportHeight(height)) {
      setResolvedHeight(height);
      return undefined;
    }

    if (typeof window === "undefined") {
      setResolvedHeight(calcViewportHeight());
      return undefined;
    }

    const handleResize = () => {
      setResolvedHeight(calcViewportHeight());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  const dialogcomponent = useRef(false);
  const exportFormats = ["xlsx", "pdf"];

  const thisInitialized = e => {
    e.component.alertDialog = dialogcomponent;
    componentRef.current = e.component;
    installDefaultFilterClear(e.component, props.defaultFilterValue);
    if (onInitialized) onInitialized(e);
  };

  const thisCustomizeColumns = e => {
    originCustomizeColumns(componentRef.current)(e);
    if (customizeColumns) customizeColumns(e);
  };

  function originCustomizeColumns(instance) {
    return e => {
      const first = e.find(x => x.isDeleted);
      if (first) {
        while (
          instance
            .getCombinedFilter()
            ?.find(x => x.toString().includes(first.dataField) && !x.toString().includes("or"))
        )
          instance.clearFilter();
        instance.deleteColumn(first.dataField);
      }
    };
  }

  const onToolbarPreparing = useCallback(
    e => {
      const list = [];
      if (selectorMode === "multiple")
        list.push({
          location: "after",
          template: "gridActionHeaderMultipleRender",
        });

      if (gridActionHeaderToggleRender)
        list.push({
          location: "after",
          template: "gridActionHeaderToggleRender",
        });

      if (gridActionHeaderRender)
        list.push({
          location: "after",
          template: "gridActionHeaderRender",
        });

      if (gridActionsHeaderRender)
        gridActionsHeaderRender.forEach((x, index) => {
          list.push({
            location: "after",
            template: `gridActionsHeaderRender${index}`,
          });
        });

      if (storageKeyEnterprise) {
        list.push({
          location: "after",
          template: "saveGridFilter",
        });
        list.push({
          location: "after",
          template: "loadGridFilter",
        });
      }

      e.toolbarOptions.items.unshift(...list);
    },
    [
      storageKeyEnterprise,
      gridActionHeaderRender,
      gridActionsHeaderRender,
      gridActionHeaderToggleRender,
      selectorMode,
    ]
  );

  const loadGridFilterRender = useCallback(
    () => (
      <DxLocalStorage
        load
        code={storageKeyEnterprise}
        dataGrid={innerRef}
      />
    ),
    [storageKeyEnterprise, innerRef]
  );

  const saveGridFilterRender = useCallback(
    () => (
      <DxLocalStorage
        code={storageKeyEnterprise}
        dataGrid={innerRef}
      />
    ),
    [storageKeyEnterprise, innerRef]
  );

  return (
    <>
      {!isLoading ? (
        <>
          {" "}
          <AlertDialog ref={dialogcomponent} />
          <DevextremeDataGrid
            height={resolvedHeight}
            showColumnLines
            showRowLines
            allowColumnResizing
            dataSource={dataSource}
            columnsAutoWidth
            width="100%"
            columnResizingMode="widget"
            allowColumnReordering
            rowAlternationEnabled
            useNative={false}
            showScrollbar="always"
            ref={innerRef}
            {...props}
            onSelectionChanged={e => {
              itemSelectionRef?.current?.toggleShow(e?.selectedRowsData?.length);
              if (props.onSelectionChanged) props.onSelectionChanged(e);
            }}
            onExporting={e => exportCallback(e, nameGrid)}
            onToolbarPreparing={onToolbarPreparing}
            customizeColumns={thisCustomizeColumns}
            onInitialized={thisInitialized}
            onContentReady={e => {
              if (initializeGridRef.current) {
                e.component.clearSelection();

                if (props.selectedGrid) {
                  const storedData = JSON.parse(localStorage.getItem(storageKeyEnterprise) || "{}");
                  storedData.selectedRowKeys = [];
                  storedData.filterValue = null;
                  localStorage.setItem(storageKeyEnterprise, JSON.stringify(storedData));

                  e.component.clearFilter();
                  if (props.selectRowsIds && props.selectRowsIds.length > 0) {
                    e.component.selectRows(props.selectRowsIds, false);
                    itemSelectionRef?.current?.toggleShow(props.selectRowsIds.length);
                  }
                }
              }

              initializeGridRef.current = false;

              if (props.onContentReady) props.onContentReady(e);
            }}
            showBorders={false}
          >
            {!remoteOperation ? null : (
              <RemoteOperations
                paging
                filtering
                sorting
                grouping
                summary
                groupPaging
              />
            )}

            {!selectorMode ? null : (
              <Selection
                mode={selectorMode}
                allowSelectAll={false}
                selectAllMode="page"
              />
            )}

            <Paging
              enabled
              defaultPageSize={30}
            />
            <FilterRow visible={type === "normal"} />
            <HeaderFilter visible={type === "normal"} />
            <GroupPanel visible={type === "normal" && grouping} />
            <ColumnChooser enabled={type === "normal"}>
              <ColumnChooserSearch
                enabled
                editorOptions={{
                  placeholder: "",
                  labelMode: "floating",
                  showClearButton: true,
                }}
              />
            </ColumnChooser>
            <FilterPanel visible={type === "normal"} />
            <ColumnFixing enabled={type === "normal"} />
            <LoadPanel enabled={type === "normal"} />

            <Export
              enabled={type === "normal"}
              formats={exportFormats}
              allowExportSelectedData={false}
            />

            {storageKeyEnterprise ? (
              <StateStoring
                enabled
                type="localStorage"
                storageKey={storageKeyEnterprise}
              />
            ) : null}

            <Sorting mode={type === "normal" ? "single" : "none"} />

            {columnName ? (
              <Column
                caption={t("name")}
                dataField="name"
                sortOrder="asc"
                cellRender={
                  hrefName
                    ? e => (
                        <Link
                          href={hrefName(e.data.id)}
                          color="inherit"
                          underline="always"
                        >
                          {e.data.name}
                        </Link>
                      )
                    : null
                }
              />
            ) : null}

            {children}

            {isActive ? (
              <Column
                caption={deletedGender === "F" ? t("deleted") : t("deletedMasculine")}
                dataField="isActive"
                dataType="boolean"
                trueText={t("no")}
                falseText={t("yes")}
                cellRender={e => (e.value ? t("no") : t("yes"))}
                visible={false}
              />
            ) : null}

            {columnDeletedDate ? (
              <Column
                caption={t("deletedDate")}
                dataField="deletedDate"
                dataType="date"
                format={dateTimeFormatGrid(user?.dateTimeFormat)}
                allowGrouping={false}
                visible={false}
              />
            ) : null}

            {columnCreatedDate ? (
              <Column
                caption={t("createdDate")}
                dataField="createdDate"
                dataType="date"
                format={dateTimeFormatGrid(user?.dateTimeFormat)}
                allowGrouping={false}
                visible={false}
              />
            ) : null}

            {dynamicConfigurationGet.map(field => {
              const nameData = `dynamicField.${
                field.fieldName.charAt(0).toLowerCase() + field.fieldName.slice(1)
              }`;
              switch (field.type) {
                case FIELDTYPE.boolean:
                  return (
                    <Column
                      caption={field.name}
                      dataField={nameData}
                      dataType="boolean"
                      trueText={t("yes")}
                      falseText={t("no")}
                      cellRender={e => (e.value ? t("yes") : t("no"))}
                      visible={false}
                    />
                  );
                case FIELDTYPE.text:
                  return (
                    <Column
                      caption={field.name}
                      dataField={nameData}
                      visible={false}
                    />
                  );
                case FIELDTYPE.number:
                  return (
                    <Column
                      caption={field.name}
                      dataField={nameData}
                      format={numberFormat(user?.numberFormat)}
                      dataType="number"
                      alignment="right"
                      visible={false}
                    />
                  );
                case FIELDTYPE.selectMultiple:
                  return null;
                case FIELDTYPE.select:
                  return (
                    <Column
                      caption={field.name}
                      dataField={nameData}
                      visible={false}
                    />
                  );
                case FIELDTYPE.dateTime:
                  return (
                    <Column
                      caption={field.name}
                      dataField={nameData}
                      format={dateTimeFormatGrid(user?.dateTimeFormat)}
                      dataType="date"
                      visible={false}
                    />
                  );
                default:
                  return null;
              }
            })}

            {gridActionRender ? (
              <Column
                caption=""
                minWidth={gridActionRenderWidth}
                allowFiltering={false}
                width={gridActionRenderMaxWidth}
                allowSorting={false}
                dataType="string"
                allowExporting={false}
                fixed
                fixedPosition="right"
                cellRender={gridActionRender}
                headerCellRender={gridActionHeaderInColumn}
              />
            ) : null}

            {scrolling ? <Scrolling mode={scrolling} /> : null}

            {storageKeyEnterprise ? (
              <Template
                name="loadGridFilter"
                render={loadGridFilterRender}
              />
            ) : null}

            {storageKeyEnterprise ? (
              <Template
                name="saveGridFilter"
                render={saveGridFilterRender}
              />
            ) : null}

            {gridActionHeaderRender ? (
              <Template
                name="gridActionHeaderRender"
                render={gridActionHeaderRender}
              />
            ) : null}

            {selectorMode === "multiple" ? (
              <Template
                name="gridActionHeaderMultipleRender"
                render={() => (
                  <ItemsSelectedHandler
                    ref={itemSelectionRef}
                    selectedItems={
                      initializeGridRef.current
                        ? 0
                        : innerRef?.current?.instance?.getSelectedRowsData()?.length || 0
                    }
                    onClearSelected={() => {
                      innerRef?.current?.instance?.clearSelection();
                      itemSelectionRef?.current?.toggleShow(0);
                    }}
                    children={gridActionHeaderMultipleRender}
                  />
                )}
              />
            ) : null}

            {gridActionsHeaderRender
              ? gridActionsHeaderRender.map((action, index) => (
                  <Template
                    key={`gridActionsHeaderRender${index}`}
                    name={`gridActionsHeaderRender${index}`}
                    render={action}
                  />
                ))
              : null}

            {gridActionHeaderToggleRender ? (
              <Template
                name="gridActionHeaderToggleRender"
                render={gridActionHeaderToggleRender}
              />
            ) : null}
          </DevextremeDataGrid>
        </>
      ) : null}
    </>
  );
}

DataGrid.propTypes = {
  innerRef: PropTypes.any,
  gridActionRender: PropTypes.any,
  gridActionRenderWidth: PropTypes.any,
  gridActionRenderMaxWidth: PropTypes.any,
  gridActionHeaderRender: PropTypes.any,
  gridActionsHeaderRender: PropTypes.any,
  gridActionHeaderToggleRender: PropTypes.any,
  gridActionHeaderMultipleRender: PropTypes.any,
  gridActionHeaderInColumn: PropTypes.any,
  customizeColumns: PropTypes.any,
  onInitialized: PropTypes.any,
  remoteOperation: PropTypes.bool,
  selectorMode: PropTypes.any,
  isActive: PropTypes.bool,
  deletedGender: PropTypes.oneOf(["M", "F"]),
  entityId: PropTypes.any,
  hrefName: PropTypes.any,
  columnName: PropTypes.bool,
  dataSource: PropTypes.any,
  onContentReady: PropTypes.any,
  selectedGrid: PropTypes.bool,
  selectRowsIds: PropTypes.any,
  grouping: PropTypes.bool,
  columnCreatedDate: PropTypes.bool,
  columnDeletedDate: PropTypes.bool,
  storageKey: PropTypes.string,
  nameGrid: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  type: PropTypes.string,
  children: PropTypes.any,
  scrolling: PropTypes.string,
  columnsAutoWidth: PropTypes.bool,
  onSelectionChanged: PropTypes.func,
  defaultFilterValue: PropTypes.any,
};
